/**
 * Вкладка «Обзор» карточки поля (v3 IA, эталон founder 2026-07-11).
 *
 * Раскладка — PageWithRail (layout-слой DS v1, спека 2026-07-29): основная
 * колонка + панель 30rem в ОДНОМ гриде, ниже 1024 панель падает вниз.
 *   Основная: мини-карта (Контур|NDVI) · карточка «Посев · сезон N» · виджеты
 *          «Тех. операции» + «Осмотры» (3 последних + «все →»).
 *   Панель: паспорт кратко (площадь / угодье / статус / кадастры) · «Погода»
 *          (заглушка) · «Рельеф» (ср./макс. уклон + экспозиция + «подробнее →»).
 *
 * PR-B: виджеты заменили строки-ссылки «По этому полю» из PR-A. Данные посева —
 * GET /fields/{id}/occupancy; виджеты читают свои эндпоинты (см. widgetApi.ts).
 */
import { ChevronRight, FileText, Layers, Sprout } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCropsReference } from '@/lib/map/useCropsReference';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import type { FieldDetail } from '@/components/fields/FieldOverviewTab';
import { useOccupancy } from './api';
import { GrowingGoalChips } from './GrowingGoalChips';
import { OccupancyProgress } from './OccupancyProgress';
import { lifecycleLabel, fieldStateLabel } from './constants';
import { sownUnitLeaves } from './rotationLogic';
import {
  useFieldStateLabel,
  useLandCategoryLabel,
  type CodeLabeler,
} from '@/lib/reference/fieldCatalogs';
import type { OccupancyLeaf } from './types';
import type { FieldCardTab } from './tabs';
import { FieldMiniMap } from './widgets/FieldMiniMap';
import { OperationsWidget } from './widgets/OperationsWidget';
import { InspectionsWidget } from './widgets/InspectionsWidget';
import { FieldTrialsBlock } from '@/components/science/TrialsBlocks';
import { WeatherWidget } from './widgets/WeatherWidget';
import { ReliefWidget } from './widgets/ReliefWidget';

/**
 * Легаси-подэкраны, разворачиваемые из виджетов «подробнее →» ПРЯМО внутри
 * Обзора. AK-736: «Тех. операции» больше не подэкран — своя вкладка
 * (`FieldOperationsTab`, см. `onGoto('ops')` у `OperationsWidget` ниже),
 * поэтому 'operations' из объединения ушёл.
 */
export type FieldSecondaryView = 'terrain' | 'inspections';

interface Props {
  detail: FieldDetail;
  fieldId: string;
  canWrite: boolean;
  onGoto: (tab: FieldCardTab) => void;
  onOpenSecondary: (view: FieldSecondaryView) => void;
}

function fmtHa(v?: number | null): string {
  return v != null ? `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га` : '—';
}

/**
 * AK-736 (UAT founder 21.08, карточка БМЗ-079.1): «План урожайности: 0 ц/га».
 * `planned_yield_c_ha` в API — `float | None`, второго «unset»-маркера нет —
 * так 0 неотличим от намеренного значения. Но у ПЛАНОВОЙ урожайности 0 не
 * несёт агрономического смысла (никто не планирует получить ноль ц/га — это
 * всегда «поле не заполнили», тот же класс дефекта, что уже чистили на проде,
 * см. `clear_placeholder_yields_prod_20260810.json`). Раз строка уже
 * условно-видима (родитель гейтит на `!= null`), нулю положено «не задан»,
 * а не цифра, которая читается как настоящий план.
 */
function fmtPlannedYield(v: number): string {
  return v > 0 ? `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ц/га` : 'не задан';
}

function SowingCard({
  leaf,
  cropColor,
  fieldId,
  canWrite,
  fallowLabel,
}: {
  leaf: OccupancyLeaf;
  cropColor?: string;
  fieldId: string;
  canWrite: boolean;
  /** Подпись состояния поля из справочника; null пока каталог грузится. */
  fallowLabel: CodeLabeler;
}) {
  const s = leaf.sowing;
  const isFallow = s?.occupancy_type === 'fallow';
  // Пар/АМП — занятие без культуры (АМП паром не называем, язык агронома).
  const title = isFallow
    ? fieldStateLabel(s?.fallow_kind, fallowLabel)
    : s?.crop_name ?? 'Посев';
  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden
            className="h-3 w-3 shrink-0 rounded-sm border border-border"
            style={cropColor ? { backgroundColor: cropColor } : undefined}
          />
          <span className="font-medium truncate">{leaf.name}</span>
          {leaf.area_ha != null && (
            <span className="text-xs text-muted-foreground shrink-0">
              · {fmtHa(leaf.area_ha)}
            </span>
          )}
        </div>
        {s ? (
          <Badge
            variant="secondary"
            className={
              isFallow
                ? 'bg-accent-brand/15 text-accent-brand hover:bg-accent-brand/15'
                : 'bg-primary/15 text-primary hover:bg-primary/15'
            }
          >
            {title}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            Не посеяно
          </Badge>
        )}
      </div>
      {s && !isFallow && <GrowingGoalChips goals={s.growing_goals} />}
      {s && !isFallow && s.planned_yield_c_ha != null && (
        <div className="text-xs text-muted-foreground">
          План урожайности:{' '}
          <b className="text-foreground">{fmtPlannedYield(s.planned_yield_c_ha)}</b>
        </div>
      )}
      {/* Этап двигается прямо здесь: обзор — первое, что открывает агроном,
          и «посеяли» отмечается одним тапом, без похода во вкладку (AK-434). */}
      {s && !isFallow && (
        <OccupancyProgress sowing={s} fieldId={fieldId} canWrite={canWrite} />
      )}
    </div>
  );
}

// Строка «лейбл — значение»: ровно 40px по спеке layout-слоя
// (py-2 + line-height 24 = 8+24+8). min-h-10 держит высоту, когда значение —
// Badge с меньшей собственной высотой; иначе ряды Паспорта «плывут».
function PassportRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-3 border-b border-border py-2 text-sm leading-6 last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export function FieldPassportTab({
  detail,
  fieldId,
  canWrite,
  onGoto,
  onOpenSecondary,
}: Props) {
  // AK-641 — сезон карточки берётся из ЛИНЗЫ СЕССИИ, той же, что у шапки и у
  // попапа поля на карте (`GET /session/season` — по канону ADR-060 §S8
  // единственный источник сезона для фронта).
  //
  // Было: `detail.current_season.id` из ответа `/fields/{id}/detail` — второй
  // резолвер, который отвечает НЕ ТО ЖЕ САМОЕ. По ADR-060 §S6 строка `seasons`
  // существует, только если в сезоне что-то делали: канон всегда отдаёт год и
  // границы, а `season_id` при непроматериализованном сезоне равен NULL. После
  // Ш3 (автосид 2000-2050 удалён) это обычное состояние живого тенанта. Год
  // приезжал — чип «2026 сезон» стоял; id не приезжал — occupancy оставался
  // `enabled: false`, и карточка писала «Активный сезон не выбран, выберите
  // сезон в шапке», хотя он выбран и попап карты тот же посев показывал.
  //
  // Ответ detail остаётся ЗАПАСНЫМ вариантом — на первые кадры прямого захода
  // по URL, пока линза ещё летит, и вне SeasonProvider (тесты, встраивание).
  // Год берём из того же источника, что и id, иначе подпись «сезон 2025» могла
  // бы стоять над данными 2026-го.
  const { season, loading: seasonLoading } = useActiveSeason();
  const lensSeasonId = season?.seasonId ?? null;
  const seasonId = lensSeasonId ?? detail.current_season?.id ?? null;
  const occ = useOccupancy(fieldId, seasonId);
  // Только посевные единицы: у поля с зонами ответ содержит ещё и участок
  // «всё поле» (AK-449) — карточка «Всё поле · 259,2 га — Не посеяно» над
  // зонными читалась бы как двойной счёт. Тот же список решает и показ
  // пустого состояния, чтобы гейт и карточки не разъезжались.
  const sownUnits = sownUnitLeaves(occ.data?.leaves ?? []);
  const crops = useCropsReference();
  const cropColorById = new Map((crops.data ?? []).map((c) => [c.id, c.color]));
  // Подписи угодья и состояния поля — из справочников, не из копии в коде.
  const landCategoryLabel = useLandCategoryLabel();
  const fallowLabel = useFieldStateLabel();

  const area = detail.area_ha_fact ?? detail.area_ha_accounting;
  const seasonLabel = lensSeasonId
    ? (season?.year ?? season?.code)
    : (detail.current_season?.year ??
      detail.current_season?.code ??
      season?.year);

  return (
    // Layout-слой DS v1 (спека 2026-07-29): обе колонки — ВНУТРИ одного грида
    // PageWithRail (1fr + 30rem, gap-6), поэтому первые карточки колонок
    // начинаются на одной горизонтали, а ряды не «плывут». Вертикаль внутри
    // колонок — единый gap-6 от контейнера; собственных mt/mb у секций нет.
    <PageWithRail
      className="py-4"
      rail={
        <>
          {/* Паспорт кратко */}
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="leading-6">Паспорт</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <PassportRow label="Площадь" value={fmtHa(area)} />
              <PassportRow
                label="Тип угодья"
                value={
                  landCategoryLabel(detail.land_category_code) ??
                  (detail.land_category_code ? '…' : 'Не задано')
                }
              />
              <PassportRow
                label="Статус"
                value={
                  detail.is_archived ? (
                    <Badge variant="destructive">В архиве</Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="bg-primary/15 text-primary hover:bg-primary/15"
                    >
                      {lifecycleLabel(detail.lifecycle_status)}
                    </Badge>
                  )
                }
              />
              <PassportRow label="Хозяйство" value={detail.farm_name ?? '—'} />
              {/* Кадастры — строка-ссылка на одноимённую вкладку (эталон: доступ
                  к кадастрам сохраняется на Обзоре). Высота ряда та же, 40px. */}
              <button
                type="button"
                onClick={() => onGoto('cadastre')}
                className="flex min-h-10 w-full items-center gap-2 border-b border-border py-2 text-left text-sm leading-6 transition-colors last:border-b-0 hover:text-primary"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="flex-1 text-muted-foreground">Кадастры</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="mt-3 h-8 px-2 text-primary"
                onClick={() => onGoto('mgmt')}
              >
                Управление полем
                <ChevronRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Погода — заглушка */}
          <WeatherWidget />

          {/* Рельеф */}
          <ReliefWidget
            fieldId={fieldId}
            onOpenDetails={() => onOpenSecondary('terrain')}
          />
        </>
      }
    >
      {/* ── Основная колонка ── */}
      {/* Мини-карта: Контур | NDVI */}
      <FieldMiniMap fieldId={fieldId} />

      {/* Посев */}
      <Card>
        <CardHeader className="p-4 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="flex items-center gap-2 leading-6">
              <Sprout className="h-4 w-4 text-primary" />
              Посев{seasonLabel ? ` · сезон ${seasonLabel}` : ''}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-8 px-2 text-primary"
              onClick={() => onGoto('rotation')}
            >
              Посевы
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 p-4 pt-0">
          {/* Сезон ещё летит и запасного id нет — скелетон, а не мигание
              пустотой: иначе на прямом заходе по URL карточка успевает
              сказать «не посеяно» до того, как узнает сезон. */}
          {!seasonId && seasonLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : occ.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : occ.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Не удалось загрузить посев поля.</AlertDescription>
            </Alert>
          ) : sownUnits.some((l) => l.sowing) ? (
            <>
              {sownUnits.map((leaf) => (
                <SowingCard
                  key={leaf.subzone_id ?? 'whole'}
                  leaf={leaf}
                  fieldId={fieldId}
                  canWrite={canWrite}
                  cropColor={
                    leaf.sowing?.crop_id
                      ? cropColorById.get(leaf.sowing.crop_id)
                      : undefined
                  }
                  fallowLabel={fallowLabel}
                />
              ))}
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Layers className="h-4 w-4" />
                Полная раскладка и правка — во вкладке «Посевы».
              </p>
            </>
          ) : (
            // Сюда же попадает непроматериализованный сезон (`season_id` =
            // NULL): по канону ADR-060 §S6 это дословно «в этом сезоне ещё
            // никто ничего не делал», то есть посевов нет — и говорить надо
            // именно это, а не гнать человека выбирать уже выбранный сезон.
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Поле ещё не посеяно в этом сезоне.
              {canWrite && (
                <div className="mt-2">
                  <Button size="sm" onClick={() => onGoto('rotation')}>
                    Посеять
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

        {/* Истории: тех. операции + осмотры. Горизонтальный gutter — тот же
          24px, что и вертикальный шаг колонки: ряды совпадают. */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* AK-736: «все →» ведёт на полноценную вкладку «Тех. операции»
            (onGoto), а не на легаси-подэкран Обзора (onOpenSecondary). */}
        <OperationsWidget fieldId={fieldId} onOpenAll={() => onGoto('ops')} />
        <InspectionsWidget
          fieldId={fieldId}
          onOpenAll={() => onOpenSecondary('inspections')}
        />
      </div>
      {/* AK-938: опыты на поле — блок обзора, новой вкладки нет (low-fi 7·Б). */}
      <FieldTrialsBlock fieldId={fieldId} farmId={detail.farm_id} canPlace={canWrite} />
    </PageWithRail>
  );
}
